/**
 * Small display helpers shared by the Home, History, Setup and Settings screens.
 * Pure functions; text comes from i18n keys passed through `t`.
 */
import { getPreset, type RuleSet, type RuleSetId } from '@/core';

type T = (key: string, options?: Record<string, unknown>) => string;

/** Uppercase section label (relaxed for Tamil, which has no case). */
export const LABEL =
  'text-xs font-semibold uppercase tracking-[0.08em] text-muted ' +
  '[html[lang=ta]_&]:normal-case [html[lang=ta]_&]:tracking-normal [html[lang=ta]_&]:text-[13px]';

export const CARD = 'rounded-card border border-line bg-surface p-[18px]';

/** A text box or select on a form. */
export const INPUT =
  'min-h-[48px] w-full min-w-0 rounded-xl border border-line bg-surface-2 px-3 text-[17px] text-fg';

/** Small status pill. */
export const CHIP =
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-line ' +
  'bg-surface-2 px-2.5 py-0.5 text-[13px] font-semibold text-muted';

/** Regulation periods for a stored rules id, or undefined for custom rules. */
export function presetPeriods(rulesId: RuleSetId): number | undefined {
  return getPreset(rulesId)?.periods;
}

/**
 * Short period label: Q1–Q4 for 4-period games, P1… otherwise, OT / OT2 for overtime.
 * `periods` undefined (custom rules, unknown) falls back to P{n}.
 */
export function periodLabel(t: T, period: number, periods: number | undefined): string {
  const p = Math.max(1, period);
  if (periods !== undefined && p > periods) {
    const n = p - periods;
    return n === 1 ? t('period.overtimeShort') : t('period.overtimeShortN', { number: n });
  }
  return periods === 4
    ? t('period.quarterShort', { number: p })
    : t('period.periodShort', { number: p });
}

/** Date and time in the UI language, always with Western digits. */
export function formatDateTime(ms: number, lang: string): string {
  try {
    return new Intl.DateTimeFormat(lang, {
      dateStyle: 'medium',
      timeStyle: 'short',
      numberingSystem: 'latn',
    }).format(ms);
  } catch {
    return new Date(ms).toLocaleString();
  }
}

/** "4 × 10 min · Overtime 5 min · Shot clock 24 / 14 s" */
export function rulesSummary(t: T, r: RuleSet): string {
  return [
    t('rules.summary.periods', { periods: r.periods, minutes: r.periodLengthSec / 60 }),
    t('rules.summary.overtime', { minutes: r.overtimeLengthSec / 60 }),
    r.shotClock.enabled
      ? t('rules.summary.shotClock', { full: r.shotClock.fullSec, short: r.shotClock.resetSec })
      : t('rules.summary.shotClockOff'),
  ].join(' · ');
}
